import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Server,
    Monitor,
    MoreVertical,
    Edit,
    Trash,
    Plus,
} from "lucide-react";
import GatewayFormModal from "../components/GatewayFormModal";

// Menü componenti
function Menu({ gateway, onEdit, onDelete }) {
    const [open, setOpen] = useState(false);

    return (
        <div className="relative inline-block text-left">
            <button
                onClick={() => setOpen(!open)}
                className="p-1 rounded-full hover:bg-gray-100"
            >
                <MoreVertical className="w-5 h-5 text-gray-500" />
            </button>

            {open && (
                <div
                    className="absolute right-0 mt-2 w-40 bg-white rounded-md shadow-lg border z-10"
                    onMouseLeave={() => setOpen(false)}
                >
                    <button
                        onClick={() => {
                            onEdit(gateway);
                            setOpen(false);
                        }}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                        <Edit className="w-4 h-4" /> Gateway Düzenle
                    </button>
                    <button
                        onClick={() => {
                            onDelete(gateway);
                            setOpen(false);
                        }}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-100"
                    >
                        <Trash className="w-4 h-4" /> Gateway Sil
                    </button>
                </div>
            )}
        </div>
    );
}

export default function AdminGateways() {
    const { customerId: paramId } = useParams();
    const customerId = paramId || localStorage.getItem("customerId")
    const [gateways, setGateways] = useState([]);
    const [editGateway, setEditGateway] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const API_BASE = process.env.REACT_APP_API_BASE_URL;
    const token = localStorage.getItem("token");
    const navigate = useNavigate();

    // Gatewayleri çek
    async function fetchGateways() {
        try {
            const url = `${API_BASE}/gateways/by-customer/${customerId}`;
            const res = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const text = await res.text();
            const data = JSON.parse(text);

            setGateways(data.gateways || data || []);
        } catch (err) {
            console.error("❌ Fetch gateways error:", err);
            setGateways([]);
        }
    }

    useEffect(() => {
        fetchGateways();
    }, [API_BASE, token, customerId]);

    // toplam cihaz sayısı
    const totalDevices = gateways.reduce(
        (sum, gw) => sum + Number(gw.device_count || 0),
        0
    );

    // Silme
    async function handleDelete(gateway) {
        if (!window.confirm(`Gateway silinsin mi? (${gateway.name})`)) return;
        try {
            const res = await fetch(`${API_BASE}/gateways/${gateway.id}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Silme başarısız");
            fetchGateways(); // listeyi yenile
        } catch (err) {
            console.error("❌ Delete gateway error:", err);
        }
    }



    return (
        <div className="p-8 min-h-screen bg-gray-50">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-gray-800">
                    Müşteriye Ait Gatewayler
                </h1>
                <button
                    onClick={() => {
                        setEditGateway(null);
                        setShowForm(true);
                    }}
                    className="flex items-center gap-2 bg-indigo-500 text-white px-4 py-2 rounded-md hover:bg-indigo-600"
                >
                    <Plus className="w-4 h-4" />
                    Gateway Ekle
                </button>
            </div>

            {/* Özet Kart */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="flex bg-white rounded-xl shadow-md overflow-hidden w-full sm:w-64">
                    <div className="bg-orange-500 flex items-center justify-center w-20">
                        <Monitor className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1 text-center py-4">
                        <p className="text-2xl font-bold text-gray-800">{totalDevices}</p>
                        <p className="text-gray-500 text-sm">Toplam Cihaz</p>
                    </div>
                </div>
            </div>

            {/* Gateway Kartları */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {gateways.map((gw) => (
                    <div
                        key={gw.id}
                        className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition relative"
                    >
                        {/* Menü */}
                        <div className="absolute top-3 right-3">
                            <Menu
                                gateway={gw}
                                onEdit={() => {
                                    setEditGateway(gw);
                                    setShowForm(true);
                                }}
                                onDelete={handleDelete}
                            />
                        </div>

                        <div className="flex items-center space-x-3 mb-3">
                            <div className="bg-indigo-100 text-indigo-600 p-3 rounded-lg">
                                <Server className="w-6 h-6" />
                            </div>
                            <h2 className="text-lg font-semibold text-gray-800">
                                {gw.name}
                            </h2>
                        </div>

                        {/* Model bilgisi + cihaz sayısı */}
                        <p className="text-xs text-gray-500 mb-2">
                            Model:{" "}
                            <span className="font-medium">{gw.model || "Bilinmiyor"}</span> •{" "}
                            {gw.device_count || 0} cihaz
                        </p>

                        {gw.description && (
                            <p className="text-gray-500 text-sm mb-3">{gw.description}</p>
                        )}

                        {gw.last_comm ? (
                            <p className="text-xs text-gray-400 mb-3">
                                Son Haberleşme:{" "}
                                {new Date(gw.last_comm).toLocaleString("tr-TR", {
                                    dateStyle: "short",
                                    timeStyle: "short",
                                })}
                            </p>
                        ) : (
                            <p className="text-xs text-red-400 mb-3">Son Haberleşme: Yok</p>
                        )}

                        {/* Butonlar */}
                        <div className="flex gap-2">
                            <button
                                onClick={() => navigate(`/admin/gateways/${gw.id}/devices`)}
                                className="flex-1 text-xs bg-indigo-500 text-white px-3 py-2 rounded-md hover:bg-indigo-600"
                            >
                                Cihazlar
                            </button>

                        </div>
                    </div>
                ))}
            </div>

            {showForm && (
                <GatewayFormModal
                    gateway={editGateway}
                    customerId={customerId}
                    onClose={() => {
                        setShowForm(false);
                        setEditGateway(null);
                    }}
                    onSaved={fetchGateways}   // ✅ dikkat: onSaved kullan
                />
            )}
        </div>
    );
}
